import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import {
  type GetOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGetData,
  type GetOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGetErrors,
  type GetOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGetResponse,
  getOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGet,
} from '../../generated';

export type GetOriginalPdpSnapshotQueryVariables =
  GetOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGetData['path'] & {
    organization_id: string;
  };

export type GetOriginalPdpSnapshotQuery = GetOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGetResponse;

export const useGetOriginalPdpSnapshotQuery = <
  TData = GetOriginalPdpSnapshotQuery,
  TError = GetOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGetErrors[keyof GetOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGetErrors],
>(
  { organization_id, portfolio_id, pdp_id }: GetOriginalPdpSnapshotQueryVariables,
  options?: Omit<UseQueryOptions<GetOriginalPdpSnapshotQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<GetOriginalPdpSnapshotQuery, TError, TData>({
    queryKey: ['GetOriginalPdpSnapshot', { organization_id, portfolio_id, pdp_id }],
    queryFn: async () => {
      const { data } = await getOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGet({
        path: { portfolio_id, pdp_id },
        throwOnError: true,
      });
      return data;
    },
    ...options,
  });
};

useGetOriginalPdpSnapshotQuery.getKey = ({
  organization_id,
  portfolio_id,
  pdp_id,
}: GetOriginalPdpSnapshotQueryVariables) => ['GetOriginalPdpSnapshot', { organization_id, portfolio_id, pdp_id }];

useGetOriginalPdpSnapshotQuery.fetcher = (
  { portfolio_id, pdp_id }: GetOriginalPdpSnapshotQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data } = await getOriginalPdpSnapshotV1PortfoliosPortfolioIdPdpsPdpIdOriginalGet({
      path: { portfolio_id, pdp_id },
      throwOnError: true,
    });
    return data;
  };
};

useGetOriginalPdpSnapshotQuery.schema = {
  method: 'get',
  path: '/v1/portfolios/{portfolio_id}/pdps/{pdp_id}/original',
};
